import * as THREE from 'three';
import { TransactionShip } from './TransactionShip';
import { BlockPlanet } from './BlockPlanet';
import { OrbitController } from './OrbitController';
import { ShipPool } from '../pools/ShipPool';

export interface DockingInfo {
  ship: TransactionShip;
  planet: BlockPlanet;
  dockingPoint: THREE.Vector3;
  startTime: number;
  dockingDuration: number;
  phase: 'approaching' | 'docking' | 'docked';
}

export class DockingManager {
  private approachingShips: Map<string, DockingInfo>;
  private shipPool: ShipPool;
  private orbitController: OrbitController;
  private dockingRadius: number;
  private approachSpeed: number;

  constructor(shipPool: ShipPool, orbitController: OrbitController) {
    this.approachingShips = new Map();
    this.shipPool = shipPool;
    this.orbitController = orbitController;
    this.dockingRadius = 2.0; // Distance from planet surface for docking
    this.approachSpeed = 0.5; // Speed multiplier for final approach
  }

  public initiateDocking(ship: TransactionShip, targetBlockHeight: number): boolean {
    const planet = this.orbitController.getPlanetByHeight(targetBlockHeight);
    if (!planet) {
      // If target planet doesn't exist, find closest planet
      const closestPlanet = this.orbitController.getClosestPlanet(ship.mesh.position);
      if (!closestPlanet) {
        return false;
      }
      this.startDocking(ship, closestPlanet);
    } else {
      this.startDocking(ship, planet);
    }
    
    return true;
  }

  private startDocking(ship: TransactionShip, planet: BlockPlanet): void {
    const dockingPoint = this.calculateDockingPoint(planet);
    
    const dockingInfo: DockingInfo = {
      ship,
      planet,
      dockingPoint,
      startTime: Date.now(),
      dockingDuration: 2000, // 2 seconds
      phase: 'approaching'
    };
    
    this.approachingShips.set(ship.id, dockingInfo);
    planet.addDockingShip(ship.id);
  }

  private calculateDockingPoint(planet: BlockPlanet): THREE.Vector3 {
    // Calculate a random point on the planet's surface
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    
    const x = Math.sin(phi) * Math.cos(theta);
    const y = Math.sin(phi) * Math.sin(theta);
    const z = Math.cos(phi);
    
    const point = new THREE.Vector3(x, y, z);
    point.multiplyScalar(planet.radius + this.dockingRadius);
    point.add(planet.getPosition());
    
    return point;
  }

  public update(deltaTime: number): void {
    const completedDockings: string[] = [];
    
    for (const [shipId, dockingInfo] of this.approachingShips) {
      this.updateDocking(dockingInfo, deltaTime);
      
      if (dockingInfo.phase === 'docked') {
        const elapsed = Date.now() - dockingInfo.startTime;
        if (elapsed >= dockingInfo.dockingDuration) {
          completedDockings.push(shipId);
        }
      }
    }
    
    // Process completed dockings
    completedDockings.forEach(shipId => {
      this.completeDocking(shipId);
    });
  }

  private updateDocking(dockingInfo: DockingInfo, deltaTime: number): void {
    const { ship, planet, dockingPoint } = dockingInfo;
    
    switch (dockingInfo.phase) {
      case 'approaching':
        this.updateApproach(dockingInfo, deltaTime);
        break;
        
      case 'docking':
        this.updateDockingAnimation(dockingInfo, deltaTime);
        break;
        
      case 'docked':
        // Ship is docked, waiting for duration to complete
        this.updateDockedState(dockingInfo);
        break;
    }
  }

  private updateApproach(dockingInfo: DockingInfo, deltaTime: number): void {
    const { ship, planet, dockingPoint } = dockingInfo;
    
    // Update docking point as planet moves
    const newDockingPoint = this.calculateDockingPoint(planet);
    dockingInfo.dockingPoint.copy(newDockingPoint);
    
    // Calculate direction to docking point
    const direction = new THREE.Vector3()
      .subVectors(newDockingPoint, ship.mesh.position)
      .normalize();
    
    const distance = ship.mesh.position.distanceTo(newDockingPoint);
    
    if (distance < 1.0) {
      // Close enough, start docking animation
      dockingInfo.phase = 'docking';
    } else {
      // Move toward docking point
      const moveDistance = ship.baseSpeed * this.approachSpeed * deltaTime;
      ship.mesh.position.add(direction.multiplyScalar(moveDistance));
      
      // Orient ship toward docking point
      ship.mesh.lookAt(newDockingPoint);
      
      // Update trail
      ship.trail.addPoint(ship.mesh.position.clone());
    }
  }

  private updateDockingAnimation(dockingInfo: DockingInfo, _deltaTime: number): void {
    const { ship, planet } = dockingInfo;
    
    // Snap to docking point
    ship.mesh.position.copy(dockingInfo.dockingPoint);
    
    // Orient toward planet center
    ship.mesh.lookAt(planet.getPosition());
    
    // Visual feedback
    ship.engineGlow.setIntensity(2.0); // Boost engine glow
    planet.flashActivity(); // Flash planet activity
    
    // Transition to docked state
    dockingInfo.phase = 'docked';
  }

  private updateDockedState(dockingInfo: DockingInfo): void {
    const { ship, planet } = dockingInfo;
    
    // Keep ship at docking point as planet moves
    const planetPos = planet.getPosition();
    const offset = dockingInfo.dockingPoint.clone().sub(planetPos).normalize();
    offset.multiplyScalar(planet.radius + this.dockingRadius);
    
    ship.mesh.position.copy(planetPos).add(offset);
    ship.mesh.lookAt(planetPos);
  }

  private completeDocking(shipId: string): void {
    const dockingInfo = this.approachingShips.get(shipId);
    if (!dockingInfo) return;
    
    const { ship, planet } = dockingInfo;
    
    // Remove from tracking
    this.approachingShips.delete(shipId);
    planet.removeDockingShip(shipId);
    
    // Visual completion effect
    this.onDockingComplete(ship, planet);
  }

  private onDockingComplete(ship: TransactionShip, planet: BlockPlanet): void {
    // Flash effect on planet
    planet.activity.surfaceGlow = 1.0;
    
    // Add activity ring
    planet.flashActivity();
    
    // Return ship to pool
    ship.deactivate();
    this.shipPool.release(ship);
  }

  public getApproachingShipsCount(): number {
    return this.approachingShips.size;
  }

  public isDocking(shipId: string): boolean {
    return this.approachingShips.has(shipId);
  }

  public cancelDocking(shipId: string): void {
    const dockingInfo = this.approachingShips.get(shipId);
    if (!dockingInfo) return;
    
    const { ship, planet } = dockingInfo;
    
    this.approachingShips.delete(shipId);
    planet.removeDockingShip(shipId);
    
    // Return ship to pool
    ship.deactivate();
    this.shipPool.release(ship);
  }

  public dispose(): void {
    // Cancel all dockings
    for (const shipId of this.approachingShips.keys()) {
      this.cancelDocking(shipId);
    }
    
    this.approachingShips.clear();
  }
}